import type { FC } from "react";

import { isRecord } from "@arti/utilities";

import type { RepositoryIndexingInsight } from "../../types";

import { Badge } from "../common/Badge";
import { Text } from "../common/Text";
import {
  type DetailSection,
  type RepoSnippetLineRangeView,
  type RepoSnippetsView,
  createSections,
  readRepoSnippets,
  readString,
  resolveProfileMetadata,
} from "./repositoryProfileMetadata";

const SnippetCodeBlock: FC<{ snippet: RepoSnippetLineRangeView }> = ({
  snippet,
}) => {
  const lines = snippet.codeSnippet
    .split("\n")
    .map((line) => line.replace(/\t/g, "  "));
  const hasContent = lines.some((line) => line.length > 0);

  if (!hasContent) {
    return (
      <p className="rounded-none border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
        Code snippet text is unavailable for this range.
      </p>
    );
  }

  return (
    <div className="custom-scrollbar max-h-72 overflow-auto rounded-none border border-slate-300 bg-white shadow-inner dark:border-2 dark:border-slate-700 dark:bg-slate-950/70">
      <div className="min-w-max font-mono text-xs">
        {lines.map((line, index) => (
          <div
            key={`${snippet.startLine}-${index}`}
            className="grid grid-cols-[3.5rem_minmax(0,1fr)] border-b border-gray-100 last:border-b-0 dark:border-white/5"
          >
            <div className="select-none border-r border-gray-100 px-2 py-1 text-right text-[11px] text-gray-500 dark:border-white/5 dark:text-white/35">
              {snippet.startLine + index}
            </div>
            <pre className="overflow-visible whitespace-pre px-3 py-1 text-[12px] leading-relaxed text-gray-800 dark:text-gray-100">
              {line || " "}
            </pre>
          </div>
        ))}
      </div>
    </div>
  );
};

const RepoSnippetsSection: FC<{ snippets: RepoSnippetsView }> = ({
  snippets,
}) => (
  <div className="space-y-3">
    <div className="rounded-none border border-emerald-500/30 bg-emerald-50/60 px-4 py-3 dark:border-emerald-500/30 dark:bg-emerald-500/10">
      <Text
        as="p"
        variant="label"
        className="text-emerald-700 dark:text-emerald-300"
      >
        Context Snippets
      </Text>
      <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
        {snippets.summary}
      </p>
    </div>

    <div className="space-y-3">
      {snippets.references.map((reference) => (
        <div
          key={reference.id}
          className="rounded-none border border-slate-300 bg-white/70 p-3 dark:border-slate-700 dark:bg-arti-panel"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="border border-slate-300 bg-slate-100 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-slate-800 dark:border-slate-700 dark:bg-arti-background dark:text-slate-100">
              {reference.path}
            </span>
            <Text
              variant="label"
              className="rounded-none border border-slate-300 px-2 py-0.5 text-gray-700 dark:border-slate-700 dark:text-white/70"
            >
              {reference.pathKind}
            </Text>
            {reference.lineRanges.length > 0 && (
              <Text
                variant="label"
                className="rounded-none border border-emerald-500/30 px-2 py-0.5 text-emerald-700 dark:border-emerald-500/30 dark:text-emerald-300"
              >
                {reference.lineRanges.length} range
                {reference.lineRanges.length === 1 ? "" : "s"}
              </Text>
            )}
          </div>

          <p className="mt-2 text-sm leading-relaxed text-gray-700 dark:text-white/75">
            {reference.whyItMatters}
          </p>

          {reference.reusableElements.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {reference.reusableElements.map((element) => (
                <Text
                  key={`${reference.id}-${element}`}
                  variant="label"
                  className="border border-emerald-500/30 bg-emerald-50 px-2 py-0.5 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300"
                >
                  {element}
                </Text>
              ))}
            </div>
          )}

          {reference.lineRanges.length > 0 && (
            <div className="mt-3 space-y-2">
              {reference.lineRanges.map((lineRange) => (
                <LineRangeBlock
                  key={`${reference.id}-${lineRange.startLine}-${lineRange.endLine}`}
                  lineRange={lineRange}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

const LineRangeBlock: FC<{ lineRange: RepoSnippetLineRangeView }> = ({
  lineRange,
}) => (
  <div>
    <div className="mb-1 flex items-center justify-between">
      <p className="text-xs font-medium text-gray-600 dark:text-white/65">
        Lines {lineRange.startLine}-{lineRange.endLine}
      </p>
    </div>
    <SnippetCodeBlock snippet={lineRange} />
  </div>
);

const SectionCard: FC<{ section: DetailSection }> = ({ section }) => (
  <div className="rounded-none border border-slate-300 bg-white/50 p-3 dark:border-slate-700 dark:bg-arti-panel">
    <Text as="p" variant="label" className="text-gray-700 dark:text-white/80">
      {section.title}
    </Text>
    <div className="mt-2 space-y-2">
      {section.rows.map((row) => (
        <div key={`${section.title}-${row.label}`} className="text-xs">
          <p className="font-medium text-gray-700 dark:text-white/80">
            {row.label}
          </p>
          <p className="text-gray-600 dark:text-white/65">
            {row.values.join(" • ")}
          </p>
        </div>
      ))}
    </div>
  </div>
);

export const RepositoryIdentityInsightDetails: FC<{
  insight: RepositoryIndexingInsight;
}> = ({ insight }) => {
  const rootMetadata = isRecord(insight.metadata) ? insight.metadata : null;
  const metadata = resolveProfileMetadata(rootMetadata);
  const repoSnippets = readRepoSnippets(rootMetadata);
  const sections = createSections(metadata);
  const role = readString(metadata, ["identity", "repository_role"]);
  const cloudProvider = readString(metadata, [
    "infrastructure",
    "cloud",
    "provider",
  ]);
  const ciPlatform = readString(metadata, ["operations", "ci_cd", "platform"]);
  const packageRegistry = readString(metadata, [
    "tooling",
    "package_management",
    "registry",
  ]);
  const defaultBranch = readString(metadata, ["development", "default_branch"]);

  const summaryBadges = [
    insight.category ? `Domain: ${insight.category}` : null,
    role ? `Role: ${role}` : null,
    insight.primary_language ? `Language: ${insight.primary_language}` : null,
    insight.architecture_pattern
      ? `Pattern: ${insight.architecture_pattern}`
      : null,
    defaultBranch ? `Default branch: ${defaultBranch}` : null,
    cloudProvider ? `Cloud: ${cloudProvider}` : null,
    ciPlatform ? `CI/CD: ${ciPlatform}` : null,
    packageRegistry ? `Registry: ${packageRegistry}` : null,
  ].filter((badge): badge is string => Boolean(badge));

  if (summaryBadges.length === 0 && sections.length === 0 && !repoSnippets) {
    return null;
  }

  return (
    <div className="mt-3 space-y-3 border-t border-gray-100 pt-3 dark:border-white/5">
      {summaryBadges.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {summaryBadges.map((badge) => (
            <Badge key={badge} variant="default" size="sm">
              {badge}
            </Badge>
          ))}
        </div>
      )}

      {sections.length > 0 && (
        <div className="grid gap-2 lg:grid-cols-2">
          {sections.map((section) => (
            <SectionCard key={section.title} section={section} />
          ))}
        </div>
      )}

      {repoSnippets && <RepoSnippetsSection snippets={repoSnippets} />}
    </div>
  );
};
